// Helpers for the Home dashboard (design: components/home/shared.tsx and
// state/analytics.ts). The design's analytics store is sample data; here are
// only the date maths, formats and the mapping of backend rows to the
// design's vocabulary.
import axios from "axios";
import config from "../../config";
import { DAY, NO_DATA, inr } from "./format";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
/** GET an API path; resolves to the JSON body, or null when the call fails or is aborted. */
export const getJson = (path, signal) => axios.get(`${config.API_BASE_URL}${path}`, { ...auth(), signal }).then((r) => r.data).catch(() => null);

export const RANGE_OPTIONS = [
  { id: "today", label: "Today", days: 1 },
  { id: "7d", label: "7 days", days: 7 },
  { id: "30d", label: "30 days", days: 30 },
  { id: "month", label: "This month", days: 24 },
];
export const rangeStart = (range, now) => {
  if (range === "month") return new Date(new Date(now).getFullYear(), new Date(now).getMonth(), 1).getTime();
  return new Date(new Date(now).getFullYear(), new Date(now).getMonth(), new Date(now).getDate()).getTime() - (range === "today" ? 0 : (range === "7d" ? 6 : 29) * DAY);
};
export const previousStart = (range, now) => rangeStart(range, now) - (range === "today" ? 1 : range === "7d" ? 7 : 30) * DAY;
export const rangePhrase = (range) => ({ today: "today", "7d": "the last 7 days", "30d": "the last 30 days", month: "this month" })[range];

export const number = (value) => (value === null || value === undefined ? NO_DATA : Number(value).toLocaleString("en-IN"));
export const percent = (value) => (value === null || value === undefined ? NO_DATA : `${Math.round(value)}%`);
export const dateLabel = (ts) => new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
export const timeLabel = (ts) => new Date(ts).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });

// Currency. Every amount the backend keeps for the clinic is in rupees; there
// is no exchange-rate API, so other currencies are listed but convert nothing.
export const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "AED" },
  { code: "GBP", symbol: "£" },
  { code: "EUR", symbol: "€" },
];
/**
 * The Home dashboard's display currency (item 138). Amounts are kept in rupees
 * and converted at the rate from GET /api/fx?base=INR; Home sets it before its
 * tabs render. Rupees until a rate is known.
 */
let DISPLAY = { code: "INR", symbol: "₹", rate: 1 };
export function setDisplayCurrency(code, rate) {
  const cur = CURRENCIES.find((c) => c.code === code);
  DISPLAY = code !== "INR" && cur && Number(rate) > 0 ? { code, symbol: cur.symbol, rate: Number(rate) } : { code: "INR", symbol: "₹", rate: 1 };
}
const sym = () => (DISPLAY.symbol.length > 1 ? `${DISPLAY.symbol} ` : DISPLAY.symbol);
export const money = (value) => {
  if (value === null || value === undefined) return NO_DATA;
  if (DISPLAY.code === "INR") return inr(value);
  const v = Number(value) * DISPLAY.rate;
  return `${sym()}${v.toLocaleString("en-IN", { maximumFractionDigits: Math.abs(v) < 100 ? 2 : 0 })}`;
};
export const compactMoney = (value) => {
  const v = Number(value) * DISPLAY.rate;
  return Math.abs(v) >= 1000 ? `${sym()}${Math.round(v / 1000)}k` : `${sym()}${Math.round(v)}`;
};
/** The display currency's code, for chart labels. */
export const displayCurrency = () => DISPLAY.code;
/** Olli credits: the wallet is kept in US dollars. */
export const credits = (usd) => (usd === null || usd === undefined ? NO_DATA : `$${Math.abs(Number(usd)).toFixed(2)}`);

/** Local midnight of a DATE column, whether it arrives as "YYYY-MM-DD" or an ISO timestamp. */
export function localDay(value) {
  if (!value) return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).getTime(); }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
const ymdOf = (value) => { const t = localDay(value); if (t === null) return ""; const d = new Date(t); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const clockMinutes = (time) => { const [h, m] = String(time || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
export const ts = (value) => (value ? new Date(value).getTime() : null);

// Appointment status → the design's wording.
const APPT_STATUS = {
  scheduled: "Booked", requested: "Booked", confirmed: "Confirmed", checked_in: "Checked in", in_progress: "Checked in",
  completed: "Completed", cancelled: "Cancelled", no_show: "No-show", rescheduled: "Rescheduled",
};
const APPT_TYPE = { consultation: "Consultation", follow_up: "Follow-up", procedure: "Procedure", emergency: "Emergency", telemedicine: "Telemedicine", lab_test: "Lab test", other: "Visit" };
export const NOT_HAPPENING = ["Cancelled", "No-show", "Rescheduled"];
export const BOOKED_STATUSES = ["Booked", "Confirmed", "Checked in"];

/** An /api/appointments row in the design's shape. */
export function toAppointment(row) {
  const day = localDay(row.appointment_date);
  const start = day === null ? null : day + clockMinutes(row.appointment_time) * 60_000;
  const checkedIn = ts(row.checked_in_at);
  const seen = row.actual_start_time && day !== null ? day + clockMinutes(row.actual_start_time) * 60_000 : null;
  return {
    id: row.id,
    start,
    duration: Number(row.duration_minutes) || 30,
    status: APPT_STATUS[row.status] || "Booked",
    doctorId: row.doctor_id,
    doctor: row.doctor_name || "Unassigned",
    patientId: row.patient_id,
    patient: [row.patient_first_name, row.patient_last_name].filter(Boolean).join(" ").trim(),
    service: row.purpose || APPT_TYPE[row.appointment_type] || "Visit",
    facility: row.facility_name || "",
    waitMinutes: checkedIn && seen && seen >= checkedIn ? (seen - checkedIn) / 60_000 : null,
  };
}

export function appointmentCounts(items) {
  return items.reduce((counts, item) => {
    if (BOOKED_STATUSES.includes(item.status)) counts.booked += 1;
    else if (item.status === "Completed") counts.completed += 1;
    else if (item.status === "Cancelled") counts.cancelled += 1;
    else if (item.status === "No-show") counts.noShow += 1;
    return counts;
  }, { booked: 0, completed: 0, cancelled: 0, noShow: 0 });
}

/** Active doctor shifts on one day: [{ doctorId, doctor, start, end, facilityId }] (start/end in minutes). */
export function shiftsOn(schedules, dayMs) {
  const weekday = new Date(dayMs).getDay();
  const date = new Date(dayMs);
  const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return (schedules || [])
    .filter((s) => Number(s.day_of_week) === weekday && s.is_active !== 0 && s.is_active !== false)
    // effective_from/until are DATEs sent as server-local midnight; slicing the string
    // read them a day early on an IST server, so a shift ran one day past its end.
    .filter((s) => (!s.effective_from || ymdOf(s.effective_from) <= iso) && (!s.effective_until || ymdOf(s.effective_until) >= iso))
    .map((s) => ({ doctorId: s.doctor_id, doctor: s.doctor_name || "Doctor", start: clockMinutes(s.start_time), end: clockMinutes(s.end_time), startLabel: String(s.start_time).slice(0, 5), endLabel: String(s.end_time).slice(0, 5), facilityId: s.facility_id }));
}

/** Every doctor on the roster or with an appointment, by id. */
export function doctorList(schedules, appointments) {
  const map = new Map();
  (schedules || []).forEach((s) => { if (!map.has(s.doctor_id)) map.set(s.doctor_id, s.doctor_name || "Doctor"); });
  (appointments || []).forEach((a) => { if (a.doctorId && !map.has(a.doctorId)) map.set(a.doctorId, a.doctor); });
  return [...map.entries()].map(([id, name]) => ({ id, name }));
}

// Lead sources → the design's channel buckets (state/analytics LEAD_SOURCES).
// WhatsApp leads sit with website forms and every other source with referrals,
// as in the design; the labels say so.
export const LEAD_BUCKETS = [
  { key: "metaAds", label: "Meta ads", sources: ["meta_leads", "meta_facebook"] },
  { key: "instagram", label: "Instagram", sources: ["meta_instagram"] },
  { key: "google", label: "Google", sources: ["google_business", "google_ads"] },
  { key: "websiteForm", label: "Website form and WhatsApp", sources: ["website_form", "whatsapp"] },
  { key: "walkIn", label: "Walk-in", sources: ["walk_in"] },
  { key: "referral", label: "Referral and other", sources: ["referral", "manual", "phone_call", "other", "(none)"] },
];
export const bucketOf = (source) => (LEAD_BUCKETS.find((b) => b.sources.includes(source)) || LEAD_BUCKETS[LEAD_BUCKETS.length - 1]).key;

// Lead pipeline stages, by the system status a clinic's own status maps to.
export const CONTACTED_OR_LATER = ["contacted", "qualified", "proposal", "negotiation", "won"];
export const QUALIFIED_OR_LATER = ["qualified", "proposal", "negotiation", "won"];

// AI agent conversation channels → the design's channels (state/analytics CHANNELS).
export const aiChannel = (channel) => (channel === "whatsapp" ? "WhatsApp" : channel === "web_voice" || channel === "voice_call" ? "Voice" : "Web widget");
const AGENT_CHANNEL = { whatsapp: "WhatsApp", web: "Web widget", web_widget: "Web widget", website: "Web widget", web_voice: "Voice", voice: "Voice", voice_call: "Phone", ivr: "Phone" };
export function agentChannels(agent) {
  let list = agent.enabled_channels;
  if (typeof list === "string") { try { list = JSON.parse(list); } catch (_) { list = null; } }
  const keys = Array.isArray(list) ? list : Object.entries(list || {}).filter(([, on]) => on).map(([k]) => k);
  const fromFlags = [agent.website_enabled && "web_widget", agent.whatsapp_enabled && "whatsapp"].filter(Boolean);
  const labels = [...new Set((keys.length ? keys : fromFlags).map((k) => AGENT_CHANNEL[k] || k))];
  return labels.join(", ") || NO_DATA;
}
/** An agent answers at this location when it is org-wide or set to it. */
export const agentInScope = (agent, facilityId) => !facilityId || agent.facility_scope !== "facility" || Number(agent.facility_id) === Number(facilityId);

/** Rows of a created_day × source lead pivot: [{ day (local midnight), source, count }]. */
export function leadRows(cells) {
  if (!cells) return null;
  return Object.entries(cells).map(([key, cell]) => {
    const [day, source] = key.split("||");
    return { day: localDay(day), source, count: Number(cell.count) || 0 };
  }).filter((r) => r.day !== null);
}
const sumCounts = (rows) => rows.reduce((n, r) => n + r.count, 0);
/** Leads created in [start, end). */
export const leadsBetween = (rows, start, end) => (rows ? sumCounts(rows.filter((r) => r.day >= start && r.day < end)) : null);

/** Lead counts per pipeline stage from a status pivot, using each clinic status's system mapping. */
export function stageCounts(cells, statuses) {
  if (!cells) return null;
  const system = Object.fromEntries((statuses || []).map((s) => [s.key_name, s.maps_to_system || s.key_name]));
  const rows = Object.entries(cells).map(([key, cell]) => ({ status: system[key.split("||")[0]] || key.split("||")[0], count: Number(cell.count) || 0 }));
  const total = rows.reduce((n, r) => n + r.count, 0);
  const at = (stages) => rows.filter((r) => stages.includes(r.status)).reduce((n, r) => n + r.count, 0);
  return { total, contacted: at(CONTACTED_OR_LATER), qualified: at(QUALIFIED_OR_LATER), won: at(["won"]) };
}
